/**
 * Operator portal rework PR 6 (migration 0212) — queries and mutations for an operator company's
 * Plan card (/v1/platform/operator-plans). Every mutation answers with the fresh plan, which
 * replaces the cached one. The operator twin of tenants/plan-hooks.ts.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import type { OperatorFeatureId, OperatorPlanView, OperatorTemplate } from '@lodex-pro/shared';

export const OPERATOR_PLAN_KEY = (tenantId: string): readonly unknown[] => [
  'platform',
  'operator-plan',
  tenantId,
];

export interface OperatorPlanResponse {
  plan: OperatorPlanView;
}

export function useOperatorPlan(
  tenantId: string,
): ReturnType<typeof useApiQuery<OperatorPlanResponse>> {
  const opts = useApiOpts();
  return useApiQuery<OperatorPlanResponse>(opts, `/v1/platform/operator-plans/${tenantId}`, {
    queryKey: OPERATOR_PLAN_KEY(tenantId),
    staleTime: 15_000,
  });
}

/** Writes the response's plan into the cache, and refreshes what shows the plan name. */
export function useStoreOperatorPlan(tenantId: string): (res: OperatorPlanResponse) => void {
  const qc = useQueryClient();
  return (res) => {
    qc.setQueryData(OPERATOR_PLAN_KEY(tenantId), res);
    // The portal card shows "plan <tier>", which a template change also writes (until PR 13).
    void qc.invalidateQueries({ queryKey: ['platform', 'operator-companies'] });
  };
}

export interface ApplyOperatorTemplateBody {
  template: OperatorTemplate;
  reason: string;
  clearOverrides: boolean;
}

export function useApplyOperatorTemplate(
  tenantId: string,
): ReturnType<typeof useApiMutation<ApplyOperatorTemplateBody, OperatorPlanResponse>> {
  const opts = useApiOpts();
  return useApiMutation<ApplyOperatorTemplateBody, OperatorPlanResponse>(
    opts,
    `/v1/platform/operator-plans/${tenantId}/template`,
    'PUT',
  );
}

export interface SeatLimitBody {
  userSeatLimit: number | null;
  reason: string;
}

export function useSetOperatorSeatLimit(
  tenantId: string,
): ReturnType<typeof useApiMutation<SeatLimitBody, OperatorPlanResponse>> {
  const opts = useApiOpts();
  return useApiMutation<SeatLimitBody, OperatorPlanResponse>(
    opts,
    `/v1/platform/operator-plans/${tenantId}/seat-limit`,
    'PUT',
  );
}

export interface OperatorOverrideBody {
  enabled: boolean;
  /** null on a limited feature = unlimited. */
  limit: number | null;
  expiresAt: string | null;
  reason: string;
}

export function useSetOperatorFeature(
  tenantId: string,
  featureId: OperatorFeatureId,
): ReturnType<typeof useApiMutation<OperatorOverrideBody, OperatorPlanResponse>> {
  const opts = useApiOpts();
  return useApiMutation<OperatorOverrideBody, OperatorPlanResponse>(
    opts,
    `/v1/platform/operator-plans/${tenantId}/features/${featureId}`,
    'PUT',
  );
}

export function useResetOperatorFeature(
  tenantId: string,
  featureId: OperatorFeatureId,
): ReturnType<
  typeof useApiMutation<{ reason: string }, OperatorPlanResponse & { cleared: boolean }>
> {
  const opts = useApiOpts();
  return useApiMutation<{ reason: string }, OperatorPlanResponse & { cleared: boolean }>(
    opts,
    `/v1/platform/operator-plans/${tenantId}/features/${featureId}/reset`,
    'POST',
  );
}
